import { z } from 'zod';

/**
 * Wave 10 — threaded comments anchored to any entity.
 *
 * Polymorphic via `(entityType, entityId)`. Visibility gate lets the
 * portals render only the subset meant for external eyes:
 *   - internal: only logged-in staff members (admin/PM/eng/foreman) see it
 *   - owner:    owner portal can see + post
 *   - buyer:    buyer portal can see + post (scoped to their unit)
 *   - public:   anyone with a link (rare — almost always use owner/buyer)
 *
 * Author info is a snapshot because users can be renamed / offboarded and
 * we want the conversation history to stay coherent.
 *
 * No replies (threading) in MVP — users post flat comments. If they want
 * to reference a previous comment they quote it in the body.
 */

export const commentEntityTypeSchema = z.enum([
  'project',
  'stage',
  'daily_log',
  'issue',
  'measurement',
  'purchase_order',
  'change_order',
  'document',
  'photo',
  // FIX-380TC-26 (worker-loop-20260509T1334Z): Obra Freedom v1 stress test
  // surfaced that comments anchored to RNC findings were collapsing to
  // 'issue' as a workaround, polluting downstream filters. Adding rnc_item
  // gives the front-end a clean handle for thread filtering and audit.
  'rnc_item',
]);
export type CommentEntityType = z.infer<typeof commentEntityTypeSchema>;

export const commentVisibilitySchema = z.enum([
  'internal',
  'owner',
  'buyer',
  'public',
]);
export type CommentVisibility = z.infer<typeof commentVisibilitySchema>;

export const commentSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  projectId: z.string(),
  entityType: commentEntityTypeSchema,
  entityId: z.string(),
  body: z.string(),
  visibility: commentVisibilitySchema,
  authorUserId: z.string(),
  authorRole: z.string(),
  authorDisplay: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type Comment = z.infer<typeof commentSchema>;

export const createCommentRequestSchema = z.object({
  entityType: commentEntityTypeSchema,
  // E2E smoke caught a bug here: `z.string()` allows the empty string,
  // which orphans the comment (no entity to attach to). Require at
  // least one non-whitespace character. Trim is applied so a payload
  // of "  " also fails validation rather than silently coercing.
  entityId: z.string().trim().min(1, 'entityId é obrigatório'),
  body: z.string().trim().min(1).max(5000),
  visibility: commentVisibilitySchema.optional(), // default: internal
});
export type CreateCommentRequest = z.infer<typeof createCommentRequestSchema>;

export const listCommentsQuerySchema = z.object({
  entityType: commentEntityTypeSchema.optional(),
  // Same reasoning as above: an empty-string filter means "match
  // anything" at the SQL layer, which is a foot-gun. If the caller
  // wants to list comments without filtering, they must omit the
  // parameter entirely.
  entityId: z.string().trim().min(1).optional(),
  visibility: commentVisibilitySchema.optional(),
});
export type ListCommentsQuery = z.infer<typeof listCommentsQuerySchema>;
